/**
 * Shared pieces of the Tasks tool: pills, avatars, dates, grouping, and the
 * props every view (List, Cards, Gantt, Overview) receives.
 */
import React from 'react';
import { differenceInCalendarDays, format, isToday, isYesterday } from 'date-fns';
import type {
  Task,
  TaskMeta,
  TaskPerson,
  TaskPriority,
  TaskProject,
  TaskQuery,
  TaskStatus,
} from '@/client/api/tasks/tasks';

/** Meta's priority pills: solid colour, white text. */
export const PRIORITY: Record<TaskPriority, { label: string; short: string; cls: string }> = {
  ubn: { label: 'Unbreak now', short: 'UBN', cls: 'bg-red-700 text-white' },
  high: { label: 'High', short: 'High', cls: 'bg-red-500 text-white' },
  mid: { label: 'Mid', short: 'Mid', cls: 'bg-orange-500 text-white' },
  low: { label: 'Low', short: 'Low', cls: 'bg-amber-400 text-white' },
  wishlist: { label: 'Wishlist', short: 'Wish', cls: 'bg-teal-500 text-white' },
  none: { label: 'No priority', short: 'None', cls: 'text-gray-400' },
};
export const PRIORITY_ORDER: TaskPriority[] = ['ubn', 'high', 'mid', 'low', 'wishlist', 'none'];

export const STATUS: Record<TaskStatus, { label: string; cls: string; bar: string }> = {
  open: { label: 'Not started', cls: 'text-gray-400', bar: 'bg-gray-300' },
  in_progress: { label: 'In progress', cls: 'bg-emerald-500 text-white', bar: 'bg-orange-300' },
  in_review: { label: 'In review', cls: 'bg-orange-500 text-white', bar: 'bg-amber-300' },
  blocked: { label: 'Blocked', cls: 'bg-red-500 text-white', bar: 'bg-red-300' },
  done: { label: 'Done', cls: 'bg-blue-500 text-white', bar: 'bg-teal-300' },
  wontfix: { label: "Won't do", cls: 'bg-gray-400 text-white', bar: 'bg-gray-200' },
};
export const STATUS_ORDER: TaskStatus[] = ['open', 'in_progress', 'in_review', 'blocked', 'done', 'wontfix'];
export const isClosed = (s: TaskStatus) => s === 'done' || s === 'wontfix';

export const PriorityPill: React.FC<{ priority: TaskPriority; className?: string }> = ({ priority, className = '' }) => {
  const p = PRIORITY[priority] ?? PRIORITY.none;
  return (
    <span className={`inline-block whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-semibold leading-none ${p.cls} ${className}`}>
      {p.short}
    </span>
  );
};

export const StatusPill: React.FC<{ status: TaskStatus }> = ({ status }) => {
  const s = STATUS[status] ?? STATUS.open;
  return (
    <span className={`inline-block whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-semibold leading-none ${s.cls}`}>
      {status === 'open' ? 'None' : s.label}
    </span>
  );
};

const AVATAR_COLOURS = ['bg-blue-500', 'bg-emerald-500', 'bg-violet-500', 'bg-orange-500', 'bg-pink-500', 'bg-teal-600', 'bg-indigo-500', 'bg-rose-500'];

export const personName = (people: TaskPerson[] | undefined, uid: number | null | undefined) => {
  if (uid == null) return 'Unowned';
  return people?.find((p) => p.uid === uid)?.name ?? `User ${uid}`;
};

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');

/** A round initials avatar, colour stable per person; dashed circle when unowned. */
export const Avatar: React.FC<{ people?: TaskPerson[]; uid: number | null | undefined; size?: number; title?: string }> = ({
  people,
  uid,
  size = 24,
  title,
}) => {
  const style = { width: size, height: size, fontSize: Math.max(9, Math.round(size * 0.4)) };
  if (uid == null) {
    return <span title={title ?? 'Unowned'} style={style} className="inline-block shrink-0 rounded-full border border-dashed border-gray-300" />;
  }
  const name = personName(people, uid);
  return (
    <span
      title={title ?? name}
      style={style}
      className={`inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white ${AVATAR_COLOURS[uid % AVATAR_COLOURS.length]}`}
    >
      {initials(name)}
    </span>
  );
};

/** "36 minutes ago", "Yesterday 5:49pm", "Wed, 9:43am", "12/8, 4:28pm". */
export const fmtUpdated = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  if (isToday(d)) return `${Math.floor(mins / 60)} hr${mins < 120 ? '' : 's'} ago`;
  const time = format(d, 'h:mmaaa');
  if (isYesterday(d)) return `Yesterday ${time}`;
  if (differenceInCalendarDays(new Date(), d) < 7) return `${format(d, 'EEE')}, ${time}`;
  return `${format(d, 'M/d')}, ${time}`;
};

/** YYYY-MM-DD -> "Aug 12" (or "Aug 12, 2025" outside this year). */
export const fmtDay = (day: string | null | undefined) => {
  if (!day) return '';
  const d = new Date(`${day}T00:00:00`);
  return format(d, d.getFullYear() === new Date().getFullYear() ? 'MMM d' : 'MMM d, yyyy');
};

export const todayIso = () => format(new Date(), 'yyyy-MM-dd');
export const isOverdue = (t: Pick<Task, 'dueDate' | 'status'>) => !!t.dueDate && !isClosed(t.status) && t.dueDate < todayIso();
export const isDueSoon = (t: Pick<Task, 'dueDate' | 'status'>) =>
  !!t.dueDate && !isClosed(t.status) && !isOverdue(t) && differenceInCalendarDays(new Date(`${t.dueDate}T00:00:00`), new Date()) <= 3;

export interface SectionGroup {
  id: string | null; // null = tasks with no section
  name: string;
  tasks: Task[];
}

/** Group a project's tasks by its sections (in section order); tasks without a
 * section (or outside any project) come first as "Tasks". Empty sections stay. */
export const groupBySection = (tasks: Task[], project: TaskProject | undefined): SectionGroup[] => {
  const groups: SectionGroup[] = [{ id: null, name: project ? 'Tasks' : 'All tasks', tasks: [] }];
  const byId = new Map<string, SectionGroup>();
  (project?.sections ?? []).forEach((s) => {
    const g = { id: s.id, name: s.name, tasks: [] as Task[] };
    byId.set(s.id, g);
    groups.push(g);
  });
  tasks.forEach((t) => (t.sectionId && byId.get(t.sectionId) ? byId.get(t.sectionId)!.tasks : groups[0].tasks).push(t));
  return project && groups[0].tasks.length === 0 && groups.length > 1 ? groups.slice(1) : groups;
};

/** A position between two neighbours, for drag and drop and manual sort. */
export const between = (before?: number, after?: number) => {
  if (before === undefined && after === undefined) return 1024;
  if (before === undefined) return after! - 1024;
  if (after === undefined) return before + 1024;
  return (before + after) / 2;
};

/** What every view receives from the Tasks page. */
export interface TaskViewProps {
  tasks: Task[];
  loading: boolean;
  meta: TaskMeta | undefined;
  /** The project in scope, when the sidebar has one selected. */
  project: TaskProject | undefined;
  query: TaskQuery;
  selectedId: string | undefined;
  onOpen: (task: Task) => void;
  /** Start a new task inline in a section (List "+ Task", Cards "New Task"). */
  onCreate: (input: { title: string; sectionId: string | null; isMilestone?: boolean }) => Promise<void>;
}

/** Options for a searchable person picker; `extra` go first (e.g. Everyone, Me, Unowned). */
export const personOptions = (people: TaskPerson[] | undefined, extra: { value: string; label: string }[] = []) => [
  ...extra,
  ...(people ?? []).map((p) => ({ value: String(p.uid), label: p.title ? `${p.name} · ${p.title}` : p.name })),
];

export const SELECT_CLASS = 'h-8 rounded-md border border-gray-200 bg-white px-2 text-sm';
export const TEXTAREA_CLASS =
  'flex w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400';
